import React from 'react'
import styled from 'styled-components'
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import { filterOutStaff, hideEmail } from 'utils/user'

export const personName = person => {
  if (!person) return null
  const full = `${person.firstName || ''} ${person.lastName || ''}`.trim()
  return full || person.name || (person.email ? hideEmail(person.email) : null)
}

export const joinNames = names => {
  if (names.length <= 1) return names[0] || ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

const peopleNames = people => joinNames((filterOutStaff(people || []) || []).map(personName).filter(Boolean))

export const getNextStep = ({ task, submitMode, canApprove, canManage, progress, blockedMessage = null }) => {
  if (!task) return null
  const status = task.status
  const permissions = task.permissions || {}
  const submitLabel = task.requiresApproval ? 'Submit for approval' : 'Submit'

  if (submitMode) {
    const total = progress?.total ?? 0
    const answered = progress?.answered ?? 0
    const isForm = submitMode === 'form'
    return {
      kind: 'fill',
      tone: 'turn',
      title: 'Your turn',
      body: isForm
        ? `Next step: answer the questions below, then press ${submitLabel}.`
        : `Next step: this task has no form to fill in. When the work is done, press ${submitLabel}.`,
      progressText: isForm && progress
        ? (total === 0
          ? 'This form has no required questions.'
          : `${answered} of ${total} required ${total === 1 ? 'question' : 'questions'} answered`)
        : null,
      primary: isForm && total > 0 && answered === 0 ? 'start' : 'submit',
      blockedMessage: isForm ? blockedMessage : null,
      submitLabel
    }
  }

  if (status === 'awaiting_approval') {
    if (canApprove) {
      return {
        kind: 'approve',
        tone: 'turn',
        title: 'Waiting for your approval',
        body: 'This task is waiting for your approval. Check the answers below, then Approve or Reject.'
      }
    }
    const names = peopleNames(task.approvers)
    return {
      kind: 'waiting',
      tone: 'neutral',
      title: 'Submitted',
      body: names
        ? `Submitted. Waiting for approval from ${names}.`
        : 'Submitted. Waiting for approval from a designated approver.'
    }
  }

  if (status === 'unassigned') {
    return {
      kind: 'blocked',
      tone: 'neutral',
      title: 'Not assigned yet',
      body: canManage
        ? 'This task needs to be assigned before the form can be filled in. Add someone in the people section below.'
        : 'This task needs to be assigned before the form can be filled in. Ask someone who manages this task to assign it.'
    }
  }

  if (['assigned', 'in_progress'].includes(status)) {
    if (!permissions.canSubmit) {
      const names = peopleNames(task.assignees)
      return {
        kind: 'blocked',
        tone: 'neutral',
        title: 'View only',
        body: names
          ? `Only the people assigned to this task can fill it in and submit it. It is assigned to ${names}.`
          : 'Only the people assigned to this task can fill it in and submit it.'
      }
    }
    return {
      kind: 'blocked',
      tone: 'neutral',
      title: 'Not ready to submit',
      body: 'This task cannot be submitted from here yet. Ask someone who manages this task to check its form.'
    }
  }

  return null
}

const ICONS = {
  fill: EditNoteOutlinedIcon,
  approve: FactCheckOutlinedIcon,
  waiting: HourglassEmptyIcon,
  blocked: InfoOutlinedIcon
}

const TaskNextStep = ({ step, onSubmit, onStart, onApprove, onReject }) => {
  if (!step) return null
  const Icon = ICONS[step.kind] || InfoOutlinedIcon

  return (
    <Outer>
      <Strip $tone={step.tone} data-tour-id='taskDetail.nextStep' data-testid='task-next-step' role='region' aria-label='What to do next'>
        <IconTile $tone={step.tone} aria-hidden='true'><Icon /></IconTile>
        <Text>
          <Title>{step.title}</Title>
          <Body>{step.body}</Body>
          {step.progressText && <Progress>{step.progressText}</Progress>}
          {step.blockedMessage && (
            <BlockedNote role='alert'>
              <WarningAmberOutlinedIcon aria-hidden='true' />
              <span>{step.blockedMessage}</span>
            </BlockedNote>
          )}
        </Text>
        {step.kind === 'fill' && (
          <Buttons>
            {step.primary === 'start'
              ? <PrimaryBtn type='button' onClick={onStart}>Start</PrimaryBtn>
              : <PrimaryBtn type='button' onClick={onSubmit}>{step.submitLabel}</PrimaryBtn>}
          </Buttons>
        )}
        {step.kind === 'approve' && (
          <Buttons>
            <SecondaryBtn type='button' onClick={onReject}>
              <CancelOutlinedIcon aria-hidden='true' />
              Reject
            </SecondaryBtn>
            <PrimaryBtn type='button' onClick={onApprove}>
              <CheckCircleOutlineIcon aria-hidden='true' />
              Approve
            </PrimaryBtn>
          </Buttons>
        )}
      </Strip>
    </Outer>
  )
}

export default TaskNextStep

const Outer = styled.div`
  box-sizing: border-box;
  max-width: 900px;
  margin: 0 auto;
  padding: 16px 24px 0;

  @media (max-width: 640px) {
    padding: 12px 16px 0;
  }
`

const Strip = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 14px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${({ $tone }) => ($tone === 'turn' ? '#eaf8fa' : '#f8fafc')};
  border: 1px solid ${({ $tone }) => ($tone === 'turn' ? '#bfe9ee' : '#e2e8f0')};
`

const IconTile = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
  color: ${({ $tone }) => ($tone === 'turn' ? '#1f8b98' : '#475569')};

  svg { font-size: 22px; }
`

const Text = styled.div`
  flex: 1 1 260px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const Title = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors?.haiti || '#0C1229'};
`

const Body = styled.span`
  font-size: 15px;
  line-height: 1.45;
  color: #334155;
`

const Progress = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
`

const BlockedNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 6px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  color: #78350f;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;

  svg { font-size: 18px; flex: 0 0 auto; margin-top: 1px; }
`

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 0 0 auto;

  @media (max-width: 640px) {
    width: 100%;

    button { flex: 1 1 auto; justify-content: center; }
  }
`

const baseBtn = `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 10px 18px;
  border-radius: 10px;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  svg { font-size: 19px; }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`

const PrimaryBtn = styled.button`
  ${baseBtn}
  border: 1px solid #33becc;
  background: #33becc;
  color: #fff;

  &:hover { background: #2aa6b3; border-color: #2aa6b3; }
`

const SecondaryBtn = styled.button`
  ${baseBtn}
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0C1229;

  &:hover { background: #f1f5f9; }
`
